import type { IntlFormatters } from 'react-intl-next';
import { defineMessages } from 'react-intl-next';

import type {
	ExtensionConfiguration,
	ToolbarExtensionConfiguration,
} from '@atlaskit/editor-plugin-selection-extension';
import TemplateIcon from '@atlaskit/icon-lab/core/template';

import { expVal } from '@confluence/feature-experiments';

/**
 * Options for building the snippet extension configuration.
 */
type BuildSnippetExtensionOptions = {
	/** Intl formatMessage function for i18n */
	formatMessage: IntlFormatters['formatMessage'];
	/** Handler called when user clicks create snippet */
	onCreateSnippet: () => void;
};

const messages = defineMessages({
	createBlockTemplate: {
		id: 'snippets.createBlockTemplate',
		defaultMessage: 'Create block template',
		description: 'Label for the create block template button in the contextual toolbar',
	},
	createBlockTemplateLozenge: {
		id: 'snippets.createBlockTemplateLozenge',
		defaultMessage: 'New',
		description: 'Lozenge label for the create block template button in the contextual toolbar',
	},
});

/**
 * Builds the snippet extension configuration for the selection extension.
 *
 * Pure function that returns the menu items for the selection extension
 * toolbar and block menu. The actual creation logic (getting selection,
 * analytics, showing modal) is provided by the consumer via the
 * `onCreateSnippet` handler.
 *
 * @returns Extension configuration array (empty if feature is disabled)
 */
export const buildSnippetExtension = ({
	formatMessage,
	onCreateSnippet,
}: BuildSnippetExtensionOptions): ExtensionConfiguration[] => {
	if (!expVal('cc_snippets', 'isEnabled', false)) {
		return [];
	}

	const toolbarConfiguration: ToolbarExtensionConfiguration = {
		getMenuItems: () => {
			return [
				{
					label: formatMessage(messages.createBlockTemplate),
					lozenge: {
						label: formatMessage(messages.createBlockTemplateLozenge),
					},
					icon: TemplateIcon,
					onClick: onCreateSnippet,
				},
			];
		},
	};

	/**
	 * placement: 'featured' allows the button to show up on the top level of the
	 * block menu, instead of under the Turn Into -> Create subsection
	 */
	const blockMenuConfiguration = {
		placement: 'featured' as const,
		getMenuItems: () => {
			return [
				{
					label: formatMessage(messages.createBlockTemplate),
					lozenge: {
						label: formatMessage(messages.createBlockTemplateLozenge),
					},
					icon: TemplateIcon,
					onClick: onCreateSnippet,
				},
			];
		},
	};

	return [
		{
			key: 'create-snippet',
			source: 'first-party',
			inlineToolbar: toolbarConfiguration,
			blockMenu: blockMenuConfiguration,
		},
	];
};
